import type { ReactNode } from 'react';
import { Navigate, Outlet, useLocation } from 'react-router';
import type { Role } from '@shared/types';
import { useAuth } from './use-auth';

function FullPageSpinner() {
  return (
    <div className="auth-page" role="status" aria-live="polite">
      <span className="spinner" aria-hidden /> <span className="sr-only">Loading</span>
    </div>
  );
}

/** Wraps routes that need a signed-in, active user. Redirects to /sign-in and remembers the target. */
export function RequireAuth({ children }: { children?: ReactNode }) {
  const { user, profile, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageSpinner />;
  if (!user || !profile) return <Navigate to="/sign-in" replace state={{ from: location }} />;
  if (profile.mustChangePassword && location.pathname !== '/change-password') {
    return <Navigate to="/change-password" replace state={{ forced: true }} />;
  }
  return children ? <>{children}</> : <Outlet />;
}

/** Wraps routes limited to the listed roles. Must sit inside RequireAuth. */
export function RequireRole({ roles, children }: { roles: Role[]; children?: ReactNode }) {
  const { role } = useAuth();
  if (!role || !roles.includes(role)) return <Navigate to="/forbidden" replace />;
  return children ? <>{children}</> : <Outlet />;
}

/** For /sign-in: a signed-in user is sent to the app instead. */
export function RedirectIfAuthed({ children }: { children: ReactNode }) {
  const { user, profile, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullPageSpinner />;
  if (user && profile) {
    const from = (location.state as { from?: { pathname?: string } } | null)?.from?.pathname;
    return <Navigate to={from && from !== '/sign-in' ? from : '/'} replace />;
  }
  return <>{children}</>;
}
